import { useState, useEffect, useRef, Fragment } from "react";
import {
  fetchMemberList,
  fetchMemberDetail,
  createMember,
  updateMember,
  deleteMember,
  uploadMemberExcel,
  fetchUploadJobStatus,
  fetchJobCodeList,
  fetchDivisionSubNameList,
  fetchDivisionSubListFromTable,
  restoreMember,
  downloadMemberExcel,
  fetchVoterList,
  searchMemberSection,
  createDivisionSub, 
  updateDivisionSub, 
  deleteDivisionSub,
  fetchQuotaList, saveQuota, deleteQuota, fetchStatusReport,
  resetMemberPassword
} from "../api/memberApi";
import Toast from "../components/Toast";

// 페이지 번호 목록 생성 (현재 페이지 주변 + 처음/끝, 나머지는 ... 처리)
const getPageNumbers = (current, total, siblingCount = 15) => {
  const pages = [];
  const left = Math.max(2, current - siblingCount);
  const right = Math.min(total - 1, current + siblingCount);

  pages.push(1);
  if (left > 2) pages.push('...');
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 1) pages.push('...');
  if (total > 1) pages.push(total);

  return pages;
};

// ===================== 삭제 확인 모달 =====================
const DeleteModal = ({ onClose, onConfirm }) => (
  <div style={ms.overlay}>
    <div style={{ ...ms.modal, maxWidth: 360, textAlign: "center", padding: "40px 24px" }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1a1a2e", margin: "0 0 8px" }}>조합원을 삭제하시겠습니까?</h3>
      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
        <button style={ms.cancelBtn} onClick={onClose}>취소</button>
        <button style={{ ...ms.saveBtn, background: "#ff4d4f" }} onClick={onConfirm}>삭제</button>
      </div>
    </div>
  </div>
);

// ===================== 조합원 현황 리포트 모달 =====================
const StatusReportModal = ({ onClose, onOpenQuotaManage }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadReport = async () => {
    setLoading(true);
    const res = await fetchStatusReport();
    if (res.success) setData(res.data);
    setLoading(false);
  };

  useEffect(() => { loadReport(); }, []);

  const divisions = [...new Set(data.map(d => d.division_sub_name))];
  const jobs      = [...new Set(data.map(d => d.job_name))];

  const getCell = (job, div) => data.find(d => d.job_name === job && d.division_sub_name === div);

  const PASTEL_COLORS = ['#FFF9DB', '#E6F7E9', '#E3F0FF', '#F3E8FF'];  // 노랑, 초록, 파랑, 보라

  const sumByJob = (job) => {
    const rows = data.filter(d => d.job_name === job);
    return {
      quota: rows.reduce((s, r) => s + (r.quota_num || 0), 0),
      curr : rows.reduce((s, r) => s + (r.curr_num  || 0), 0),
    };
  };

  const sumByDiv = (div) => {
    const rows = data.filter(d => d.division_sub_name === div && d.union_target_yn === 'Y');   // ← 노조가입대상만
    return {
      quota: rows.reduce((s, r) => s + (r.quota_num || 0), 0),
      curr : rows.reduce((s, r) => s + (r.curr_num  || 0), 0),
    };
  };

  const grandTotal = {
    quota: data.filter(r => r.union_target_yn === 'Y').reduce((s, r) => s + (r.quota_num || 0), 0),   // ← 노조가입대상만
    curr : data.filter(r => r.union_target_yn === 'Y').reduce((s, r) => s + (r.curr_num  || 0), 0),    // ← 노조가입대상만
  };

  const rate = (curr, quota) => quota > 0 ? Math.round((curr / quota) * 100) : 0;

  return (
    <div style={ms.overlay}>
      <div style={{ ...ms.modal, maxWidth: 960, padding: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #f0f0f0" }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e", margin: 0 }}>📊 조합원 현황</h3>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={{ background: "#fff", border: "1.5px solid #1677ff", color: "#1677ff", borderRadius: 8, padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              onClick={() => onOpenQuotaManage(loadReport)}>⚙️ 정원 기준자료 관리</button>
            <button style={{ background: "none", border: "none", fontSize: 18, color: "#aaa", cursor: "pointer" }}
              onClick={onClose}>✕</button>
          </div>
        </div>

        <div style={{ padding: 20, maxHeight: 560, overflow: "auto" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "#bbb" }}>⏳ 불러오는 중...</div>
          ) : data.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#bbb", fontSize: 14 }}>
              등록된 정원 정보가 없습니다.<br/>
              <span style={{ fontSize: 12 }}>"정원 기준자료 관리"에서 먼저 등록해주세요.</span>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={sr.th}>직종</th>
                  {divisions.map((div, idx) => (
                    <th key={div} colSpan={3} style={{ ...sr.th, textAlign: "center", background: PASTEL_COLORS[idx % PASTEL_COLORS.length] }}>{div}</th>
                  ))}
                  <th colSpan={3} style={{ ...sr.th, textAlign: "center", background: "#f0f7ff" }}>합산</th>
                </tr>
                <tr>
                  <th style={sr.th}></th>
                  {divisions.map(div => (
                    <Fragment key={div}>
                      <th style={sr.subTh}>정원</th>
                      <th style={sr.subTh}>현원</th>
                      <th style={sr.subTh}>비율</th>
                    </Fragment>
                  ))}
                  <th style={{ ...sr.subTh, background: "#f0f7ff" }}>정원</th>
                  <th style={{ ...sr.subTh, background: "#f0f7ff" }}>현원</th>
                  <th style={{ ...sr.subTh, background: "#f0f7ff" }}>비율</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => {
                  const total = sumByJob(job);
                  return (
                    <tr key={job}>
                      <td style={sr.tdLabel}>{job}</td>
                      {divisions.map(div => {
                        const cell = getCell(job, div);
                        return (
                          <Fragment key={div}>
                            <td style={sr.td}>{cell ? cell.quota_num.toLocaleString() : '-'}</td>
                            <td style={sr.td}>{cell ? cell.curr_num.toLocaleString() : '-'}</td>
                            <td style={sr.td}>{cell ? `${rate(cell.curr_num, cell.quota_num)}%` : '-'}</td>
                          </Fragment>
                        );
                      })}
                      <td style={{ ...sr.td, background: "#f7faff", fontWeight: 700 }}>{total.quota.toLocaleString()}</td>
                      <td style={{ ...sr.td, background: "#f7faff", fontWeight: 700 }}>{total.curr.toLocaleString()}</td>
                      <td style={{ ...sr.td, background: "#f7faff", fontWeight: 700 }}>{rate(total.curr, total.quota)}%</td>
                    </tr>
                  );
                })}
                <tr>
                  <td style={{ ...sr.tdLabel, fontWeight: 800 }}>
                    합계
                    <div style={{ fontSize: 10, color: "#aaa", fontWeight: 400 }}>(노조가입대상 기준)</div>
                  </td>
                  {divisions.map(div => {
                    const t = sumByDiv(div);
                    return (
                      <Fragment key={div}>
                        <td style={{ ...sr.td, fontWeight: 800 }}>{t.quota.toLocaleString()}</td>
                        <td style={{ ...sr.td, fontWeight: 800 }}>{t.curr.toLocaleString()}</td>
                        <td style={{ ...sr.td, fontWeight: 800 }}>{rate(t.curr, t.quota)}%</td>
                      </Fragment>
                    );
                  })}
                  <td style={{ ...sr.td, fontWeight: 800, background: "#f0f7ff" }}>{grandTotal.quota.toLocaleString()}</td>
                  <td style={{ ...sr.td, fontWeight: 800, background: "#f0f7ff" }}>{grandTotal.curr.toLocaleString()}</td>
                  <td style={{ ...sr.td, fontWeight: 800, background: "#f0f7ff" }}>{rate(grandTotal.curr, grandTotal.quota)}%</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ===================== 정원 기준자료 관리 모달 =====================
const QuotaManageModal = ({ onClose, onSaved }) => {
  const [list, setList] = useState([]);
  const [divisionSubNames, setDivisionSubNames] = useState([]);
  const [jobNames, setJobNames] = useState([]);
  const [form, setForm] = useState({ division_sub_name: '', job_name: '', manual_yn: 'N', union_target_yn: 'Y', quota_num: 0, curr_num: 0, remark: '' });
  const [editKey, setEditKey] = useState(null);
  const [jobInputMode, setJobInputMode] = useState(false);  // 직종 직접입력 모드
  const [toast, setToast] = useState('');
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2000); };

  const loadList = async () => {
    const res = await fetchQuotaList();
    if (res.success) setList(res.data);
  };

  useEffect(() => {
    loadList();
    fetchDivisionSubNameList().then(res => { if (res.success) setDivisionSubNames(res.data.map(d => d.division_sub_name)); });
    fetchJobCodeList().then(res => { if (res.success) setJobNames(res.data.map(j => j.job_name)); });
  }, []);

  const resetForm = () => {
    setForm({ division_sub_name: '', job_name: '', manual_yn: 'N', union_target_yn: 'Y', quota_num: 0, curr_num: 0, remark: '' });
    setEditKey(null);
    setJobInputMode(false);
  };

  const handleEdit = (row) => {
    setForm({
      division_sub_name: row.division_sub_name,
      job_name: row.job_name,
      manual_yn: row.manual_yn,
      union_target_yn: row.union_target_yn || 'Y',
      quota_num: row.quota_num,
      curr_num: row.curr_num || 0,
      remark: row.remark || ''
    });
    setEditKey(`${row.division_sub_name}_${row.job_name}`);
    setJobInputMode(!jobNames.includes(row.job_name));
  };

  const handleSave = async () => {
    if (!form.division_sub_name || !form.job_name) { showToast('❌ 소속과 직종을 선택해주세요.'); return; }
    const res = await saveQuota(form);
    if (res.success) {
      showToast('✅ 저장되었습니다.');
      resetForm();
      await loadList();
      if (onSaved) onSaved();
    } else showToast(`❌ ${res.message}`);
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.division_sub_name} / ${row.job_name}" 정원 정보를 삭제하시겠습니까?`)) return;
    const res = await deleteQuota(row.division_sub_name, row.job_name);
    if (res.success) { showToast('✅ 삭제되었습니다.'); await loadList(); if (onSaved) onSaved(); }
    else showToast(`❌ ${res.message}`);
  };

  return (
    <div style={ms.overlay}>
      <Toast msg={toast} />
      <div style={{ ...ms.modal, maxWidth: 1100, width: "94vw", padding: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #f0f0f0" }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e", margin: 0 }}>⚙️ 정원 기준자료 관리</h3>
          <button style={{ background: "none", border: "none", fontSize: 18, color: "#aaa", cursor: "pointer" }}
            onClick={onClose}>✕</button>
        </div>

        {/* 입력 폼 */}
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #f0f0f0" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 0.5fr 0.5fr 90px 90px", gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>소속</label>
              <select style={{ ...qm.input, width: "100%" }} value={form.division_sub_name} disabled={!!editKey}
                onChange={e => setForm(p => ({ ...p, division_sub_name: e.target.value }))}>
                <option value="">선택</option>
                {divisionSubNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>직종</label>
              {jobInputMode ? (
                <div style={{ display: "flex", gap: 4 }}>
                  <input style={{ ...qm.input, width: "100%", boxSizing: "border-box" }}
                    value={form.job_name} placeholder="직종 직접입력" autoFocus
                    onChange={e => setForm(p => ({ ...p, job_name: e.target.value }))} />
                  <button type="button" style={qm.miniBtn} onClick={() => { setJobInputMode(false); setForm(p => ({ ...p, job_name: '' })); }}>↩</button>
                </div>
              ) : (
                <select style={{ ...qm.input, width: "100%" }} value={form.job_name} disabled={!!editKey}
                  onChange={e => {
                    if (e.target.value === '__custom__') { setJobInputMode(true); setForm(p => ({ ...p, job_name: '' })); return; }
                    setForm(p => ({ ...p, job_name: e.target.value }));
                  }}>
                  <option value="">선택</option>
                  <option value="__custom__">+ 직접입력</option>
                  {jobNames.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              )}
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>노조가입대상</label>
              <select style={{ ...qm.input, width: "100%" }} value={form.union_target_yn}
                onChange={e => setForm(p => ({ ...p, union_target_yn: e.target.value }))}>
                <option value="Y">Y</option>
                <option value="N">N</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>관리방식</label>
              <select style={{ ...qm.input, width: "100%" }} value={form.manual_yn}
                onChange={e => setForm(p => ({ ...p, manual_yn: e.target.value }))}>
                <option value="N">자동</option>
                <option value="Y">수동</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>정원</label>
              <input type="number" style={{ ...qm.input, width: "100%", boxSizing: "border-box" }} value={form.quota_num}
                onChange={e => setForm(p => ({ ...p, quota_num: Number(e.target.value) }))} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>현원</label>
              <input type="number"
                style={{ ...qm.input, width: "100%", boxSizing: "border-box", background: form.manual_yn === 'Y' ? '#fff' : '#f5f5f5', color: form.manual_yn === 'Y' ? '#1a1a2e' : '#aaa' }}
                value={form.manual_yn === 'Y' ? form.curr_num : ''}
                placeholder={form.manual_yn === 'Y' ? '' : '자동집계'}
                disabled={form.manual_yn !== 'Y'}
                onChange={e => setForm(p => ({ ...p, curr_num: Number(e.target.value) }))} />
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>메모</label>
              <input style={{ ...qm.input, width: "100%", boxSizing: "border-box" }} value={form.remark}
                onChange={e => setForm(p => ({ ...p, remark: e.target.value }))} />
            </div>
            <button style={{ background: "#1677ff", color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
              onClick={handleSave}>{editKey ? '수정 저장' : '+ 추가'}</button>
            {editKey && (
              <button style={{ background: "#f5f5f5", border: "1px solid #ddd", borderRadius: 8, padding: "8px 20px", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}
                onClick={resetForm}>취소</button>
            )}
          </div>
        </div>

        {/* 목록 */}
        <div style={{ maxHeight: 420, overflowY: "auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "0.9fr 0.9fr 0.7fr 0.9fr 0.6fr 0.6fr 1.6fr 1fr", gap: 8, padding: "10px 24px", background: "#f8faff", borderBottom: "1px solid #e8f0fe", position: "sticky", top: 0 }}>
            {["소속", "직종", "관리방식", "노조가입대상", "정원", "현원", "메모", ""].map(h => (
              <div key={h} style={{ fontSize: 12, fontWeight: 700, color: "#888", textAlign: ["정원", "현원"].includes(h) ? "right" : "left" }}>{h}</div>
            ))}
          </div>
          {list.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#bbb", fontSize: 13 }}>등록된 정원 정보가 없습니다.</div>
          ) : (
            list.map(row => (
              <div key={`${row.division_sub_name}_${row.job_name}`}
                style={{ display: "grid", gridTemplateColumns: "0.9fr 0.9fr 0.7fr 0.9fr 0.6fr 0.6fr 1.6fr 1fr", gap: 8, padding: "10px 24px", borderBottom: "1px solid #f5f5f5", alignItems: "center", fontSize: 13 }}>
                <div>{row.division_sub_name}</div>
                <div>{row.job_name}</div>
                <div>{row.manual_yn === 'Y' ? '수동' : '자동'}</div>
                <div>{row.union_target_yn === 'N' ? <span style={{ color: "#ff4d4f", fontWeight: 600 }}>N</span> : <span style={{ color: "#52c41a", fontWeight: 600 }}>Y</span>}</div>
                <div style={{ textAlign: "right" }}>{Number(row.quota_num).toLocaleString()}</div>
                <div style={{ textAlign: "right" }}>{row.manual_yn === 'Y' ? Number(row.curr_num).toLocaleString() : <span style={{ color: "#aaa" }}>자동</span>}</div>
                <div style={{ color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.remark || '-'}</div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={{ background: "#fff", border: "1px solid #1677ff", color: "#1677ff", borderRadius: 6, padding: "3px 8px", fontSize: 12, cursor: "pointer" }}
                    onClick={() => handleEdit(row)}>수정</button>
                  <button style={{ background: "#fff2f0", border: "1px solid #ffccc7", color: "#ff4d4f", borderRadius: 6, padding: "3px 8px", fontSize: 12, cursor: "pointer" }}
                    onClick={() => handleDelete(row)}>삭제</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ===================== 상세/등록 페이지 =====================
const DetailPage = ({ item, onBack, onSave, onDelete, isNew, isAdmin, userLevel }) => {
  const [form, setForm] = useState(item || {
    emp_no: "", name: "", phone: "", ssn1: "", ssn2: "",
    address: "", job_name: "", section: "",
    department: "", division_sub_name: "",
    join_dt: "", user_level: "2",
    withdraw_yn: "", withdraw_dt: "", remark: ""
  });
const [showDelete, setShowDelete] = useState(false);
const [divisionSubList, setDivisionSubList] = useState([]);
const [toast,         setToast]         = useState('');
const [sectionModal,   setSectionModal]   = useState(false);
const [sectionSearchDivSub, setSectionSearchDivSub] = useState('');
const [sectionSearchDept,   setSectionSearchDept]   = useState('');
const [sectionSearchJob,    setSectionSearchJob]    = useState('');
const [sectionResults, setSectionResults] = useState([]);
const [sectionLoading, setSectionLoading] = useState(false);
const [jobModal, setJobModal] = useState(false);
const [jobList,  setJobList]  = useState([]);
const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
const [userLevelList, setUserLevelList] = useState([]);
const [showDivisionSubModal, setShowDivisionSubModal] = useState(false);
const [newSubName, setNewSubName] = useState("");
const [editingSub, setEditingSub] = useState(null);
const [editSubValue, setEditSubValue] = useState("");

  // 소속 예시
  useEffect(() => {
    fetchDivisionSubListFromTable().then(res => { if (res.success) setDivisionSubList(res.data); });
  }, []);

  // 사용자 레벨 목록 로드
  useEffect(() => {
    const codes = localStorage.getItem('code_USER_LEVEL');
    if (codes) setUserLevelList(JSON.parse(codes));
  }, []);

  useEffect(() => { if (item) setForm(item); }, [item]);

// 지구 검색 
const runSectionSearch = async (divSub, dept, job) => {
  if (!dept.trim()) { alert('부서를 입력해주세요.'); return; }
  setSectionLoading(true);
  try {
    const res = await searchMemberSection(divSub, dept, job);
    if (res.success) setSectionResults(res.data);
    else { alert(res.message); setSectionResults([]); }
  } catch (err) {
    alert('검색 실패');
  } finally {
    setSectionLoading(false);
  }
};

// 직종 검색 모달
const openSectionModal = () => {
  const divSub = form.division_sub_name || '';
  const dept   = form.department || '';
  const job    = form.job_name || '';

  if (!dept.trim()) { alert('부서를 입력해주세요.'); return; }

  setSectionSearchDivSub(divSub);
  setSectionSearchDept(dept);
  setSectionSearchJob(job);
  setSectionResults([]);
  setSectionModal(true);
  runSectionSearch(divSub, dept, job);
};

// 직종 검색 모달
const openJobModal = async () => {
  const res = await fetchJobCodeList();
  if (res.success) {
    // distinct + 가나다순 정렬 (job_name 기준)
    const uniqueNames = [...new Set(res.data.map(j => j.job_name).filter(Boolean))];
    uniqueNames.sort((a, b) => a.localeCompare(b, 'ko'));
    setJobList(uniqueNames);
  } else {
    setJobList([]);
  }
  setJobModal(true);
};

// 직종 선택
const handleJobSelect = (jobName) => {
  set('job_name', jobName);
  setJobModal(false);
};

const loadDivisionSubList = async () => {
  const res = await fetchDivisionSubListFromTable();
  if (res.success) setDivisionSubList(res.data);
};

const handleAddSub = async () => {
  if (!newSubName.trim()) return;
  const res = await createDivisionSub(newSubName.trim());
  if (res.success) { setNewSubName(""); loadDivisionSubList(); }
  else alert(res.message);
};

const handleUpdateSub = async (code) => {
  if (!editSubValue.trim()) return;
  const res = await updateDivisionSub(code, editSubValue.trim());
  if (res.success) { setEditingSub(null); loadDivisionSubList(); }
  else alert(res.message);
};

const handleDeleteSub = async (code, name) => {
  if (!window.confirm(`"${name}" 소속을 삭제하시겠습니까?`)) return;
  const res = await deleteDivisionSub(code);
  if (res.success) loadDivisionSubList();
  else alert(res.message);
};

// 지구 검색 
const handleSectionSearch = () => runSectionSearch(sectionSearchDivSub, sectionSearchDept, sectionSearchJob);

// 지구 선택
const handleSectionSelect = (row) => {
  set('section', row.section || '');
  setSectionModal(false);
};

  // 유효성 검사 후 저장
const handleSubmit = () => {
    // 필수 항목
    if (!form.name) { alert("성명은 필수 입력 항목입니다."); return; }

    // 길이 체크
    if (form.emp_no      && form.emp_no.length      > 20)  { alert("사번은 20자 이하로 입력해주세요."); return; }
    if (form.name        && form.name.length         > 50)  { alert("성명은 50자 이하로 입력해주세요."); return; }
    if (form.division_sub_name && form.division_sub_name.length > 100) { alert("소속은 100자 이하로 입력해주세요."); return; }
    if (form.section     && form.section.length      > 10)  { alert("지구는 10자 이하로 입력해주세요."); return; }
    if (form.phone       && form.phone.length        > 20)  { alert("전화번호는 20자 이하로 입력해주세요."); return; }
    if (form.address     && form.address.length      > 200) { alert("주소는 200자 이하로 입력해주세요."); return; }
    if (form.job_name    && form.job_name.length     > 100) { alert("직종은 100자 이하로 입력해주세요."); return; }
    if (form.department  && form.department.length   > 100) { alert("부서는 100자 이하로 입력해주세요."); return; }
    if (form.remark      && form.remark.length       > 500) { alert("비고는 500자 이하로 입력해주세요."); return; }

    // 가입일자 형식 (YYYYMMDD 8자리 숫자)
    if (form.join_dt && !/^\d{8}$/.test(form.join_dt)) {
      alert("가입일자는 8자리 숫자(YYYYMMDD)로 입력해주세요."); return;
    }

    // 주민번호 형식
    if (form.ssn1 && !/^\d{6}$/.test(form.ssn1)) {
      alert("주민번호 앞자리는 6자리 숫자로 입력해주세요."); return;
    }
    if (form.ssn2 && !/^\d{7}$/.test(form.ssn2)) {
      alert("주민번호 뒷자리는 7자리 숫자로 입력해주세요."); return;
    }

    // 사번 자동채번 확인
    if (isNew && !form.emp_no) {
      const ok = window.confirm("사번을 입력하지 않았습니다.\n사번을 자동 채번하겠습니까?");
      if (!ok) return;
    }

    // 탈퇴일자 조건부 필수
    if (form.withdraw_yn === 'Y' && !form.withdraw_dt) {
      alert("탈퇴처리하는 경우에는 탈퇴일자를 입력해주세요."); return;
    }

    onSave(form);
  };

  // 간병 재직확인서 출력
  const handlePrint = () => {
    const today = new Date();
    const yyyy  = today.getFullYear();
    const mm    = String(today.getMonth() + 1).padStart(2, '0');
    const dd    = String(today.getDate()).padStart(2, '0');
    const ssn   = form.ssn1 && form.ssn2 ? `${form.ssn1}-${form.ssn2[0]}******` : '';
    const joinDt = form.join_dt
      ? `${form.join_dt.slice(0,4)}년 ${form.join_dt.slice(4,6)}월 ${form.join_dt.slice(6,8)}일`
      : '가입일 미등록';

    // 직인 이미지
    const user    = JSON.parse(localStorage.getItem('user') || '{}');
    const sealImg = user.seal_img || '';
    const sealHtml = sealImg
      ? `<div style="position:relative;display:inline-block;">
           <span style="position:absolute;top:-30px;left:-40px;opacity:0.85;z-index:1;">
             <img src="${sealImg}" style="width:80px;height:80px;object-fit:contain;" />
           </span>
         </div>`
      : `<span class="seal">인</span>`;

    const printHtml = `<!DOCTYPE html>
      <html lang="ko">
      <head>
      <meta charset="utf-8" />
      <title>재직확인서</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: '맑은 고딕', 'Malgun Gothic', 'Apple SD Gothic Neo', sans-serif; padding: 70px 80px; font-size: 18px; line-height: 1.9; color: #000; width: 210mm; min-height: 297mm; }
        .title { text-align: center; font-size: 22px; font-weight: bold; letter-spacing: 12px; margin-top: 60px; margin-bottom: 36px; padding-bottom: 12px; border-bottom: 2px solid #000; }
        .info-wrap { margin-bottom: 30px; }
        .info-row { display: flex; font-size: 18px; line-height: 2.0; }
        .info-label { min-width: 60px; font-weight: normal; }
        .info-value { flex: 1; }
        .content { font-size: 18px; line-height: 2.0; margin: 30px 0; word-break: keep-all; }
        .items { margin: 16px 0 16px 4px; font-size: 18px; line-height: 2.4; }
        .item-row { display: flex; gap: 8px; }
        .item-label { min-width: 90px; font-weight: normal; }
        .date { text-align: center; margin: 60px 0 40px; font-size: 18px; letter-spacing: 2px; }
        .sign { text-align: center; font-size: 18px; line-height: 2.2; }
        .sign-main { font-size: 18px; margin-top: 4px; }
        .sign-sub { font-size: 18px; display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 2px; }
        .seal { display: inline-block; border: 1.5px solid #000; padding: 2px 10px; font-size: 16px; }
        @media print { body { padding: 50px 60px; } button { display: none !important; } @page { size: A4; margin: 0; } }
      </style>
      </head>
      <body>
        <div class="title">재 직 확 인 서</div>
        <div class="info-wrap">
          <div class="info-row"><span class="info-label">성명 :</span><span class="info-value">${form.name}${ssn ? ` (${ssn})` : ''}</span></div>
          <div class="info-row"><span class="info-label">주소 :</span><span class="info-value">${form.address || '주소 미등록'}</span></div>
        </div>
        <div class="content">사)보건복지자원연구원 희망간병은 상기자가 아래와 같이 간병사로서 근무하고 있음을 확인합니다.</div>
        <div class="items">
          <div class="item-row"><span class="item-label">■ 기간 :</span><span>${joinDt}부터 현재까지</span></div>
          <div class="item-row"><span class="item-label">■ 근무처 :</span><span>${form.department || '부서 미등록'}</span></div>
        </div>
        <div class="date">${yyyy}년 ${mm}월 ${dd}일</div>
        <div class="sign">
          <div class="sign-main">사)보건복지자원연구원 부설 무료소개소</div>
          <div class="sign-sub" style="position:relative;">
            희망간병 [직인]${sealHtml}
          </div>
        </div>
      </body>
      </html>`;

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden;';
    document.body.appendChild(iframe);
    iframe.contentDocument.open();
    iframe.contentDocument.write(printHtml);
    iframe.contentDocument.close();
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 500);
  };

  // 비밀번호 초기화 (화면에 표시된 연락처 기준)
  const handleResetPassword = async () => {
    // 1. 연락처 검증 먼저
    const phoneMatch = form.phone ? String(form.phone).match(/^(\d{2,3})-(\d{3,4})-(\d{4})$/) : null;
    if (!phoneMatch) {
      setToast('❌ 전화번호가 등록되어 있지 않거나 형식이 올바르지 않습니다. (예: 010-1234-5678)');
      setTimeout(() => setToast(''), 2500);
      return;
    }

    // 2. 검증 통과 후 확인
    if (!window.confirm(`"${form.name}"님의 비밀번호를 전화번호 뒷자리 4자리로 초기화하시겠습니까?`)) return;

    // 3. 서버 요청
    const res = await resetMemberPassword(form.emp_no, form.phone);
    if (res.success) {
      setToast(`✅ ${res.message}`);
    } else {
      setToast(`❌ ${res.message}`);
    }
    setTimeout(() => setToast(''), 2500);
  };

  return (
    <div style={styles.wrap}>
      <Toast msg={toast} />
      <button style={styles.backBtn} onClick={onBack}>← 목록으로</button>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>{isNew ? "👤 조합원 등록" : "👤 조합원 상세정보"}</h1>
      </div>

      <div style={{ ...styles.card, padding: 32 }}>

        {/* ========== 기본사항 ========== */}
        <div style={detailStyles.sectionTitle}>기본사항</div>
        <div style={detailStyles.grid}>

          {/* 사번 */}
          <div style={detailStyles.field}>
            <label style={detailStyles.label}>사번</label>
            {isNew
              ? <input style={detailStyles.input} value={form.emp_no} onChange={e => set("emp_no", e.target.value)} placeholder="사번 입력 (미입력 시 자동채번)" />
              : <input style={{ ...detailStyles.input, background: "#f5f5f5", color: "#aaa" }} value={form.emp_no} disabled />
            }
          </div>

          {/* 성명 */}
          <div style={detailStyles.field}>
            <label style={detailStyles.label}>성명 <span style={{ color: "#ff4d4f" }}>*</span></label>
            <input style={detailStyles.input} value={form.name || ""} onChange={e => set("name", e.target.value)} placeholder="성명 입력" />
          </div>

          {/* 소속 */}
          <div style={detailStyles.field}>
            <label style={{ ...detailStyles.label, display: "flex", alignItems: "center", gap: 4, overflow: "hidden" }}>
              <span style={{ flexShrink: 0 }}>소속</span>
              <button type="button"
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#888", padding: 0, flexShrink: 0 }}
                onClick={() => setShowDivisionSubModal(true)}
                title="소속 관리">⚙️</button>
              {divisionSubList.length > 0 && (
                <span style={{
                  fontSize: 11, color: "#1677ff", fontWeight: 400,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
                }}>
                  예) {divisionSubList.slice(0, 3).map(s => s.division_sub_name).join(', ')}
                  {divisionSubList.length > 3 ? ' ...' : ''}
                </span>
              )}
            </label>
            <input style={detailStyles.input} value={form.division_sub_name || ""}
              onChange={e => set("division_sub_name", e.target.value)} placeholder="소속 입력" />
          </div>

          {/* 지구 */}
          <div style={detailStyles.field}>
            <label style={{ ...detailStyles.label, display: "flex", alignItems: "center", gap: 4 }}>
              지구
              <button type="button"
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, color: "#1677ff", padding: 0 }}
                onClick={openSectionModal}
                title="지구 추천검색"
              >🔍</button>
            </label>
            <input style={detailStyles.input} value={form.section || ""}
              onChange={e => set("section", e.target.value)} placeholder="지구 입력" maxLength={10} />
          </div>

          {/* 부서 */}
          <div style={detailStyles.field}>
            <label style={detailStyles.label}>부서</label>
            <input style={detailStyles.input} value={form.department || ""} onChange={e => set("department", e.target.value)} placeholder="부서 입력" />
          </div>

          {/* 직종 */}
          <div style={detailStyles.field}>
            <label style={{ ...detailStyles.label, display: "flex", alignItems: "center", gap: 4 }}>
              직종
              <button type="button"
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, color: "#1677ff", padding: 0 }}
                onClick={openJobModal}
                title="직종 목록에서 선택"
              >🔍</button>
            </label>
            <input style={detailStyles.input} value={form.job_name || ""}
              onChange={e => set("job_name", e.target.value)} placeholder="직종 입력" />
          </div>

          {/* 전화번호 */}
          <div style={detailStyles.field}>
            <label style={detailStyles.label}>전화번호</label>
            <input style={detailStyles.input} value={form.phone || ""} onChange={e => set("phone", e.target.value)} placeholder="010-0000-0000" />
          </div>

          {/* 가입일자 */}
          <div style={detailStyles.field}>
            <label style={detailStyles.label}>가입일자</label>
            <input style={detailStyles.input} value={form.join_dt || ""} onChange={e => set("join_dt", e.target.value)} placeholder="YYYYMMDD" maxLength={8} />
          </div>

          {/* 성별 */}
          <div style={detailStyles.field}>
            <label style={detailStyles.label}>성별</label>
            <input style={detailStyles.input} value={form.gender || ""}
              onChange={e => set("gender", e.target.value)} placeholder="성별 입력 (예: 남, 여)" />
          </div>

          {/* 노조동조합 직책 */}
          <div style={detailStyles.field}>
            <label style={detailStyles.label}>노동조합 직책</label>
            <input style={detailStyles.input} value={form.union_position || ""}
              onChange={e => set("union_position", e.target.value)} placeholder="노동조합 직책 입력 (예: 대의원, 분회장)" />
          </div>

          {/* 탈퇴여부 */}
          <div style={detailStyles.field}>
            <label style={detailStyles.label}>탈퇴여부</label>
            <select style={detailStyles.input} value={form.withdraw_yn || ""}
              onChange={e => set("withdraw_yn", e.target.value)}>
              <option value="N">가입중</option>
              <option value="A">휴직</option>
              <option value="Y">탈퇴</option>
            </select>
          </div>

          {/* 탈퇴일자 */}
          <div style={detailStyles.field}>
            <label style={detailStyles.label}>탈퇴일자</label>
            <input style={{
              ...detailStyles.input,
              background: form.withdraw_yn !== 'Y' ? "#f5f5f5" : "transparent",
              color: form.withdraw_yn !== 'Y' ? "#aaa" : "#1a1a2e"
            }}
              type="date"
              value={form.withdraw_dt || ""}
              onChange={e => set("withdraw_dt", e.target.value)}
              disabled={form.withdraw_yn !== 'Y'}
            />
          </div>

          {/* 비고 */}
          <div style={{ ...detailStyles.field, gridColumn: "1 / -1" }}>
            <label style={detailStyles.label}>비고</label>
            <input style={detailStyles.input} value={form.remark || ""}
              onChange={e => set("remark", e.target.value)} placeholder="비고 입력" />
          </div>

        </div>

        {/* ========== 선택사항 ========== */}
        <div style={{ ...detailStyles.sectionTitle, marginTop: 24 }}>선택사항</div>
        <div style={detailStyles.grid}>

          {/* 주민번호 */}
          <div style={detailStyles.field}>
            <label style={detailStyles.label}>주민번호</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flex: 1, padding: "0 12px" }}>
              <input style={{ ...detailStyles.input, padding: "10px 0", flex: 1 }} value={form.ssn1 || ""} onChange={e => set("ssn1", e.target.value)} placeholder="앞 6자리" maxLength={6} />
              <span style={{ color: "#aaa" }}>-</span>
              <input style={{ ...detailStyles.input, padding: "10px 0", flex: 1 }} value={form.ssn2 || ""} onChange={e => set("ssn2", e.target.value)} placeholder="뒤 7자리" maxLength={7} />
            </div>
          </div>

          {/* 사용자 레벨 */}
          <div style={detailStyles.field}>
            <label style={detailStyles.label}>사용자 레벨</label>
            <select style={{
              ...detailStyles.input,
              background: "#f5f5f5",
              color: "#aaa",
              cursor: "not-allowed"
            }}
              value={form.user_level || "2"}
              disabled>
              {userLevelList.map(c => (
                <option key={c.code} value={c.code}>{c.code_nm}</option>
              ))}
            </select>
          </div>

          {/* 주소 */}
          <div style={{ ...detailStyles.field, gridColumn: "1 / -1" }}>
            <label style={detailStyles.label}>주소</label>
            <input style={detailStyles.input} value={form.address || ""} onChange={e => set("address", e.target.value)} placeholder="주소 입력" />
          </div>

          {/* 등록/수정 정보 */}
          {!isNew && (
            <>
              <div style={detailStyles.field}>
                <label style={detailStyles.label}>등록일자</label>
                <input style={{ ...detailStyles.input, background: "#f5f5f5", color: "#aaa" }} value={form.reg_dt || "-"} disabled />
              </div>
              <div style={detailStyles.field}>
                <label style={detailStyles.label}>최초 등록자</label>
                <input style={{ ...detailStyles.input, background: "#f5f5f5", color: "#aaa" }} value={form.reg_emp_no || "-"} disabled />
              </div>
              <div style={detailStyles.field}>
                <label style={detailStyles.label}>수정일자</label>
                <input style={{ ...detailStyles.input, background: "#f5f5f5", color: "#aaa" }} value={form.mod_dt || "-"} disabled />
              </div>
              <div style={detailStyles.field}>
                <label style={detailStyles.label}>최종 수정자</label>
                <input style={{ ...detailStyles.input, background: "#f5f5f5", color: "#aaa" }} value={form.mod_emp_no || "-"} disabled />
              </div>
            </>
          )}

        </div>

        {/* 버튼 영역 */}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 10, paddingTop: 10 }}>
          {!isNew && form.del_yn === 'Y' && isAdmin && (
            <button
              style={{ background: "#fff", border: "1.5px solid #722ed1", color: "#722ed1", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
              onClick={async () => {
                const res = await restoreMember(form.emp_no);
                if (res.success) {
                  setToast('✅ 원복되었습니다.');
                  const detailRes = await fetchMemberDetail(form.emp_no);
                  if (detailRes.success) setForm(detailRes.data);
                } else {
                  setToast(`❌ ${res.message}`);
                }
                setTimeout(() => setToast(''), 2500);   // ← 추가
              }}>
              🔄 원복
            </button>
          )}
          {!isNew && form.del_yn !== 'Y' && (
            <button style={styles.resetPwBtn} onClick={handleResetPassword}>🔑 비밀번호 초기화</button>
          )}
          {!isNew && form.del_yn !== 'Y' && (
            <button style={styles.deleteBtn} onClick={() => setShowDelete(true)}>🗑 삭제</button>
          )}
          {form.del_yn !== 'Y' && (
            <button style={styles.editBtn} onClick={handleSubmit}>
              {isNew ? "✅ 등록" : "✏️ 수정"}
            </button>
          )}
          {!isNew && form.del_yn !== 'Y' && (
          <button style={styles.printBtn} onClick={handlePrint}>🖨️ 간병 재직확인서</button>
          )}
        </div>
      </div>

      {showDelete && (
        <DeleteModal onClose={() => setShowDelete(false)} onConfirm={() => onDelete(form.emp_no)} />
      )}

      {sectionModal && (
        <div style={ms.overlay}>
          <div style={{ ...ms.modal, maxWidth: 760, padding: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #f0f0f0" }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e", margin: 0 }}>🔍 지구 추천검색</h3>
              <button style={{ background: "none", border: "none", fontSize: 18, color: "#aaa", cursor: "pointer" }}
                onClick={() => setSectionModal(false)}>✕</button>
            </div>
            <div style={{ padding: "16px 24px", borderBottom: "1px solid #f0f0f0", display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input
                style={{ flex: "1 1 140px", minWidth: 100, border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "10px 14px", fontSize: 13, outline: "none" }}
                placeholder="소속 (선택)"
                value={sectionSearchDivSub}
                onChange={e => setSectionSearchDivSub(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSectionSearch()}
              />
              <input
                style={{ flex: "1 1 140px", minWidth: 100, border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "10px 14px", fontSize: 13, outline: "none" }}
                placeholder="부서 (필수)"
                value={sectionSearchDept}
                onChange={e => setSectionSearchDept(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSectionSearch()}
              />
              <input
                style={{ flex: "1 1 140px", minWidth: 100, border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "10px 14px", fontSize: 13, outline: "none" }}
                placeholder="직종 (선택)"
                value={sectionSearchJob}
                onChange={e => setSectionSearchJob(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSectionSearch()}
              />
              <button
                style={{ background: "#1677ff", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}
                onClick={handleSectionSearch}
              >{sectionLoading ? '검색 중...' : '검색'}</button>
            </div>
            <div style={{ maxHeight: 360, overflowY: "auto" }}>
              {sectionResults.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#bbb", fontSize: 14 }}>
                  부서/직종 입력 후 검색하세요.
                </div>
              ) : (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr", padding: "10px 24px", background: "#f8faff", borderBottom: "1px solid #e8f0fe" }}>
                    {["소속", "지구", "부서", "직종", "사번", "성명"].map(h => (
                      <div key={h} style={{ fontSize: 12, fontWeight: 700, color: "#888" }}>{h}</div>
                    ))}
                  </div>
                  {sectionResults.map((r, i) => (
                    <div key={i}
                      style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr", padding: "12px 24px", borderBottom: "1px solid #f5f5f5", cursor: "pointer", transition: "background 0.15s" }}
                      onDoubleClick={() => handleSectionSelect(r)}
                      onMouseEnter={e => e.currentTarget.style.background = "#f0f7ff"}
                      onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                      <div style={{ fontSize: 13, color: "#1a1a2e" }}>{r.division_sub_name || '-'}</div>
                      <div style={{ fontSize: 13, color: "#1a1a2e", fontWeight: 600 }}>{r.section || '-'}</div>
                      <div style={{ fontSize: 13, color: "#666" }}>{r.department || '-'}</div>
                      <div style={{ fontSize: 13, color: "#666" }}>{r.job_name || '-'}</div>
                      <div style={{ fontSize: 13, color: "#666" }}>{r.emp_no || '-'}</div>
                      <div style={{ fontSize: 13, color: "#666" }}>{r.name || '-'}</div>
                    </div>
                  ))}
                </>
              )}
            </div>
            <div style={{ padding: "14px 24px", borderTop: "1px solid #f0f0f0", textAlign: "right" }}>
              <span style={{ fontSize: 12, color: "#bbb", marginRight: 12 }}>행을 더블클릭하면 지구가 자동 입력돼요</span>
              <button style={ms.cancelBtn} onClick={() => setSectionModal(false)}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {jobModal && (
        <div style={ms.overlay}>
          <div style={{ ...ms.modal, maxWidth: 400, padding: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #f0f0f0" }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e", margin: 0 }}>🔍 직종 목록</h3>
              <button style={{ background: "none", border: "none", fontSize: 18, color: "#aaa", cursor: "pointer" }}
                onClick={() => setJobModal(false)}>✕</button>
            </div>

            <div style={{ maxHeight: 420, overflowY: "auto" }}>
              {jobList.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#bbb", fontSize: 14 }}>
                  등록된 직종이 없습니다.
                </div>
              ) : (
                jobList.map((name, i) => (
                  <div key={i}
                    style={{ padding: "12px 24px", borderBottom: "1px solid #f5f5f5", cursor: "pointer", fontSize: 14, color: "#1a1a2e", transition: "background 0.15s" }}
                    onDoubleClick={() => handleJobSelect(name)}
                    onMouseEnter={e => e.currentTarget.style.background = "#f0f7ff"}
                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                    {name}
                  </div>
                ))
              )}
            </div>

            <div style={{ padding: "14px 24px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 12, color: "#bbb" }}>더블클릭하면 직종이 자동 입력돼요</span>
              <button style={ms.cancelBtn} onClick={() => setJobModal(false)}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {showDivisionSubModal && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: 0, width: 380, maxHeight: "70vh", display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #f0f0f0" }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e", margin: 0 }}>⚙️ 소속 관리</h3>
            <button style={{ background: "none", border: "none", fontSize: 18, color: "#aaa", cursor: "pointer" }}
              onClick={() => setShowDivisionSubModal(false)}>✕</button>
          </div>

          <div style={{ display: "flex", gap: 8, padding: "16px 24px", borderBottom: "1px solid #f0f0f0" }}>
            <input
              style={{ flex: 1, border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none" }}
              placeholder="새 소속명 입력"
              value={newSubName}
              onChange={e => setNewSubName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddSub()}
            />
            <button style={{ background: "#1677ff", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              onClick={handleAddSub}>추가</button>
          </div>

          <div style={{ overflowY: "auto", flex: 1 }}>
            {divisionSubList.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: "#bbb", fontSize: 14 }}>등록된 소속이 없습니다.</div>
            ) : (
              divisionSubList.map(s => (
                <div key={s.division_sub_code} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 24px", borderBottom: "1px solid #f5f5f5" }}>
                  {editingSub === s.division_sub_code ? (
                    <>
                      <input
                        style={{ flex: 1, border: "1.5px solid #1677ff", borderRadius: 6, padding: "6px 10px", fontSize: 13, outline: "none" }}
                        value={editSubValue}
                        onChange={e => setEditSubValue(e.target.value)}
                        autoFocus
                      />
                      <button style={{ background: "none", border: "none", color: "#1677ff", fontSize: 12, cursor: "pointer" }}
                        onClick={() => handleUpdateSub(s.division_sub_code)}>저장</button>
                      <button style={{ background: "none", border: "none", color: "#aaa", fontSize: 12, cursor: "pointer" }}
                        onClick={() => setEditingSub(null)}>취소</button>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 12, color: "#bbb", width: 24 }}>{s.division_sub_code}</span>
                      <span style={{ flex: 1, fontSize: 14, color: "#1a1a2e" }}>{s.division_sub_name}</span>
                      <button style={{ background: "none", border: "none", color: "#1677ff", fontSize: 12, cursor: "pointer" }}
                        onClick={() => { setEditingSub(s.division_sub_code); setEditSubValue(s.division_sub_name); }}>수정</button>
                      <button style={{ background: "none", border: "none", color: "#ff4d4f", fontSize: 12, cursor: "pointer" }}
                        onClick={() => handleDeleteSub(s.division_sub_code, s.division_sub_name)}>삭제</button>
                    </>
                  )}
                </div>
              ))
            )}
          </div>

          <div style={{ padding: "14px 24px", borderTop: "1px solid #f0f0f0", textAlign: "right" }}>
            <button style={{ border: "1px solid #e0e0e0", background: "#fff", borderRadius: 8, padding: "6px 16px", fontSize: 13, cursor: "pointer" }}
              onClick={() => setShowDivisionSubModal(false)}>닫기</button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

// ===================== 리스트 페이지 =====================
export default function MemberPage({ onLogout, userLevel }) {
  const isAdmin = ['0', '1', '3'].includes(userLevel); // 간병관리자 포함
  const [page,          setPage]          = useState("list");
  const [list,          setList]          = useState([]);
  const [selected,      setSelected]      = useState(null);
  const [isNew,         setIsNew]         = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [toast,         setToast]         = useState("");
  const [searchEmpNo,   setSearchEmpNo]   = useState("");
  const [searchName,    setSearchName]    = useState("");
  const [searchDivSub,     setSearchDivSub]     = useState("");
  const [searchDepartment, setSearchDepartment] = useState("");
  const [searchJobCode,    setSearchJobCode]    = useState("");
  const [searchSection,    setSearchSection]    = useState("");
  const [searchWithdrawYn, setSearchWithdrawYn] = useState("N");
  const [searchJoinDt,     setSearchJoinDt]     = useState("");
  const [searchJoinDtCond, setSearchJoinDtCond] = useState("");
  const [searchHasPosition, setSearchHasPosition] = useState("");
  const [divisionSubList, setDivisionSubList] = useState([]);
  const [jobCodeList,     setJobCodeList]     = useState([]);
  const [currentPage,   setCurrentPage]   = useState(1);
  const [total,         setTotal]         = useState(0);
  const [uploadResult,  setUploadResult]  = useState(null);
  const [uploading,     setUploading]     = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ processed: 0, total: 0 });
  const [uploadPhase, setUploadPhase] = useState('preprocessing');
  const [uploadMode, setUploadMode] = useState('add'); // add: 추가모드, delete: 삭제모드
  const fileInputRef = useRef(null);
  const PAGE_SIZE = 10;
  const [voterModalOpen, setVoterModalOpen] = useState(false);
  const [voterCriteria, setVoterCriteria] = useState('section_name');
  const [statusReportOpen, setStatusReportOpen] = useState(false);   // ← 추가
  const [quotaManageOpen,  setQuotaManageOpen]  = useState(false);   // ← 추가
  const [onQuotaSavedCallback, setOnQuotaSavedCallback] = useState(null);  // ← 추가

  useEffect(() => {
    loadList();
    fetchDivisionSubNameList().then(res => { if (res.success) setDivisionSubList(res.data); }); // ← 교체
    fetchJobCodeList().then(res => { if (res.success) setJobCodeList(res.data); });
  }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2500); };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const modeLabel = uploadMode === 'add' ? '추가 모드' : '삭제 모드';
    const modeDesc  = uploadMode === 'add'
      ? '중복 사번은 오류 처리되며 기존 데이터는 유지됩니다.'
      : '⚠️ 보호 대상(관리자, 탈퇴, 삭제)을 제외한 모든 조합원이 삭제된 후 신규 파일로 교체됩니다.\n이 작업은 되돌릴 수 없습니다.';

    const ok = window.confirm(`[${modeLabel}] 엑셀 파일의 데이터를 업로드합니다.\n${modeDesc}\n계속 진행하시겠습니까?`);
    if (!ok) { e.target.value = ""; return; }

    setUploading(true);
    setUploadResult(null);
    setUploadProgress({ processed: 0, total: 0 });
    setUploadPhase('preprocessing');   // ← 추가

    try {
      const accepted = await uploadMemberExcel(file, uploadMode);
      if (!accepted.success) {
        showToast(`❌ ${accepted.message}`);
        setUploading(false);
        e.target.value = "";
        return;
      }

      setUploadProgress({ processed: 0, total: accepted.total });

      // 1초 간격 폴링
      const pollTimer = setInterval(async () => {
        try {
          const res = await fetchUploadJobStatus(accepted.jobId);
          if (!res.success) {
            clearInterval(pollTimer);
            showToast(`❌ ${res.message}`);
            setUploading(false);
            return;
          }
          const job = res.data;
          setUploadPhase(job.phase);          // ← 추가
          setUploadProgress({ processed: job.processed, total: job.total });

          if (job.status === 'done') {
            clearInterval(pollTimer);
            setUploadResult({ message: job.message, errors: job.errors });
            setUploading(false);
            loadList();
            fetchDivisionSubNameList().then(res => { if (res.success) setDivisionSubList(res.data); });
            fetchJobCodeList().then(res => { if (res.success) setJobCodeList(res.data); });
          } else if (job.status === 'error') {
            clearInterval(pollTimer);
            showToast(`❌ 업로드 처리 중 오류: ${job.message}`);
            setUploading(false);
          }
        } catch (err) {
          clearInterval(pollTimer);
          showToast("❌ 진행상황 조회 실패");
          setUploading(false);
        }
      }, 400);

    } catch (err) {
      showToast("❌ 업로드 접수 실패: " + (err.response?.data?.message || err.message));
      setUploading(false);
    } finally {
      e.target.value = "";
    }
  };

  // 엑셀 다운로드 양식 생성
  const handleDownloadTemplate = () => {
    import("exceljs").then(async (ExcelJS) => {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("조합원");

      const headers = ["소속", "지구", "부서", "직종", "사번", "이름", "성별", "전화번호",
                        "가입일자(YYYYMMDD)", "비고", "노동조합 직책", "주민번호앞6자리", "주민번호뒤7자리", "주소"];
      const requiredCols = ["이름", "전화번호"]; // 필수값

      ws.addRow(headers);
      ws.addRow(["본원", "1지구", "전산실", "간호직", "EMP001", "홍길동", "남", "010-1234-5678",
                "20200101", "", "대의원", "900101", "1234567", "대구시 북구"]);

      // 헤더 스타일링
      const headerRow = ws.getRow(1);
      headerRow.eachCell((cell, colNumber) => {
        const isRequired = requiredCols.includes(headers[colNumber - 1]);
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: isRequired ? 'FFFFF3B0' : 'FFE8F0FE' }, // 필수: 연노랑 / 선택: 연파랑
        };
        cell.font = { bold: true };
        cell.alignment = { horizontal: 'center' };
        cell.border = {
          top: { style: 'thin' }, left: { style: 'thin' },
          bottom: { style: 'thin' }, right: { style: 'thin' },
        };
      });

      // 컬럼 폭 자동 조정
      ws.columns.forEach((col, i) => {
        col.width = Math.max(headers[i].length + 4, 14);
      });

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "조합원_업로드양식.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    });
  };

  // 엑셀 다운로드 - 서버에서 검색조건 전체 대상으로 생성
  const handleExcelDownload = async () => {
    try {
      await downloadMemberExcel({
        emp_no: searchEmpNo, name: searchName, div_sub: searchDivSub,
        department: searchDepartment, job_code: searchJobCode,
        section: searchSection, withdraw_yn: searchWithdrawYn
      });
    } catch (err) {
      showToast("❌ 엑셀 다운로드에 실패했습니다.");
    }
  };

  // 선거인명부 출력 - 그루핑 동적 처리용
  const VOTER_CRITERIA_CONFIG = {
    section_name:     { groupFields: ['division_sub_name', 'section'],    sortFields: ['department', 'job_name', 'name'] },
    section_empno:    { groupFields: ['division_sub_name', 'section'],    sortFields: ['department', 'job_name', 'emp_no'] },
    department_name:  { groupFields: ['division_sub_name', 'department'], sortFields: ['job_name', 'name'] },
    department_empno: { groupFields: ['division_sub_name', 'department'], sortFields: ['job_name', 'emp_no'] },
    division_name:    { groupFields: ['division_sub_name'],                sortFields: ['job_name', 'name'] },
    division_empno:   { groupFields: ['division_sub_name'],                sortFields: ['job_name', 'emp_no'] },
  };

  // 초과 글자수 1자당 0.5px씩만 완만하게 축소 (최소 9px)
  const fitFontSize = (text, baseSize = 12, maxChars = 6, minSize = 9) => {
    const len = String(text || '').length;
    if (len <= maxChars) return baseSize;
    const shrink = (len - maxChars) * 0.5;
    return Math.max(minSize, baseSize - shrink);
  };

  // 선거인명부 출력 - 검색조건에 맞는 전체 대상을 서버에서 가져옴
  const handleVoterPrint = async (criteria) => {
    let voterList;
    try {
      const res = await fetchVoterList({
        emp_no: searchEmpNo, name: searchName, div_sub: searchDivSub,
        department: searchDepartment, job_code: searchJobCode,
        section: searchSection, withdraw_yn: searchWithdrawYn
      });
      if (!res.success) { showToast(`❌ ${res.message}`); return; }
      voterList = res.data;
    } catch (err) {
      showToast("❌ 선거인명부 데이터 조회에 실패했습니다.");
      return;
    }

    const config = VOTER_CRITERIA_CONFIG[criteria];

    // 분리(그룹핑) - config.groupFields 조합으로 그룹 생성
    const groups = {};
    voterList.forEach(m => {
      const parts = config.groupFields.map(f => m[f] || '(미지정)');
      const key = parts.join('___');
      if (!groups[key]) groups[key] = { titleParts: parts, members: [] };
      groups[key].members.push(m);
    });

    // 그룹 순서 정렬 ('(미지정)'은 맨 뒤로) + 그룹 내부는 config.sortFields로 정렬
    const sortedGroups = Object.values(groups).sort((a, b) => {
      for (let i = 0; i < a.titleParts.length; i++) {
        const av = a.titleParts[i], bv = b.titleParts[i];
        if (av === '(미지정)' && bv !== '(미지정)') return 1;
        if (bv === '(미지정)' && av !== '(미지정)') return -1;
        const cmp = av.localeCompare(bv, 'ko');
        if (cmp !== 0) return cmp;
      }
      return 0;
    }).map(group => ({
      ...group,
      members: [...group.members].sort((a, b) => {
        for (const f of config.sortFields) {
          const cmp = String(a[f] || '').localeCompare(String(b[f] || ''), 'ko');
          if (cmp !== 0) return cmp;
        }
        return 0;
      }),
    }));

    // 표시 컬럼은 항상 고정: 연번/부서/직종/사번/성명/서명/비고
    const sectionHtml = sortedGroups.map(({ titleParts, members }) => {
      const rows = members.map((m, i) => `
        <tr>
          <td style="text-align:center">${i + 1}</td>
          <td style="text-align:center; font-size:${fitFontSize(m.department, 12, 8)}px">${m.department || ''}</td>
          <td style="text-align:center; font-size:${fitFontSize(m.job_name, 12, 5)}px">${m.job_name || ''}</td>
          <td style="text-align:center; font-size:${fitFontSize(m.emp_no, 12, 6)}px">${m.emp_no || ''}</td>
          <td style="text-align:center; font-weight:600; font-size:${fitFontSize(m.name, 12, 4)}px">${m.name || ''}</td>
          <td></td>
          <td></td>
        </tr>
      `).join('');

      return `
        <div class="section-wrap">
          <div class="section-title">${titleParts.join(' - ')} (${members.length}명)</div>
          <table>
            <colgroup>
              <col style="width:6%">
              <col style="width:26%">
              <col style="width:16%">
              <col style="width:11%">
              <col style="width:11%">
              <col style="width:12%">
              <col style="width:18%">
            </colgroup>
            <thead>
              <tr>
                <th>연번</th>
                <th>부서</th>
                <th>직종</th>
                <th>사번</th>
                <th>성명</th>
                <th>서명</th>
                <th>비고</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      `;
    }).join('');

    const printHtml = `<!DOCTYPE html>
    <html lang="ko">
    <head>
    <meta charset="utf-8"/>
    <title>선거인명부</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: '맑은 고딕', 'Malgun Gothic', sans-serif; font-size: 12px; color: #000; }
      .section-wrap { padding: 20px 30px; page-break-after: always; }
      .section-wrap:last-child { page-break-after: avoid; }
      .section-title { font-size: 16px; font-weight: bold; margin-bottom: 12px;
                      padding-bottom: 8px; border-bottom: 2px solid #000; }
      table { width: 100%; border-collapse: collapse; table-layout: fixed; }
      th { background: #f0f0f0; padding: 7px 8px; border: 1px solid #ccc;
          font-size: 12px; font-weight: bold; text-align: center; white-space: nowrap; overflow: hidden; }
      td { padding: 6px 8px; border: 1px solid #ddd; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      tr:nth-child(even) td { background: #fafafa; }
      tr:nth-child(even) td { background: #fafafa; }
      @media print {
        @page { size: A4 portrait; margin: 15mm; }
      }
    </style>
    </head>
    <body>${sectionHtml}</body>
    </html>`;

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden;';
    document.body.appendChild(iframe);
    iframe.contentDocument.open();
    iframe.contentDocument.write(printHtml);
    iframe.contentDocument.close();
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 500);
  };

  const loadDetail = async (emp_no) => {
    try {
      const res = await fetchMemberDetail(emp_no);
      if (res.success) { setSelected(res.data); setIsNew(false); setPage("detail"); }
      else showToast(`❌ ${res.message}`);
    } catch (err) { showToast("❌ 상세 조회 실패"); }
  };

  const loadList = async (page = currentPage) => {
    setLoading(true);
    try {
      const res = await fetchMemberList(searchEmpNo, searchName, searchDivSub, searchDepartment, searchJobCode, searchSection, searchWithdrawYn, searchJoinDt, searchJoinDtCond, searchHasPosition, page, PAGE_SIZE);
      if (res.success) { setList(res.data); setTotal(res.total); }
      else if (res.message === '토큰이 만료되었습니다. 다시 로그인해주세요.') onLogout();
    } catch (err) { showToast("❌ 목록 조회 실패"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadList(currentPage); }, [currentPage]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const handleSearch = () => { setCurrentPage(1); loadList(1); };

  const handleSave = async (form) => {
    try {
      const res = isNew
        ? await createMember(form)
        : await updateMember(form.emp_no, form);
      if (res.success) {
        await loadList();
        const empNo = isNew ? res.emp_no : form.emp_no;
        const detailRes = await fetchMemberDetail(empNo);
        if (detailRes.success) {
          setSelected(detailRes.data);
          setIsNew(false);
        }
        showToast(isNew ? "✅ 조합원 정보가 등록되었습니다." : "✅ 조합원 정보가 수정되었습니다.");
      } else showToast(`❌ ${res.message}`);
    } catch (err) { showToast("❌ 저장 실패"); }
  };

  const handleDelete = async (emp_no) => {
    try {
      const res = await deleteMember(emp_no);
      if (res.success) { await loadList(); setPage("list"); showToast("✅ 조합원 정보가 삭제되었습니다."); }
      else showToast(`❌ ${res.message}`);
    } catch (err) { showToast("❌ 삭제 실패"); }
  };

  const handleReset = () => {
    setSearchEmpNo(""); setSearchName(""); setSearchDivSub("");
    setSearchDepartment(""); setSearchJobCode(""); setSearchSection("");
    setSearchWithdrawYn("N"); setSearchJoinDt(""); setSearchJoinDtCond(""); setSearchHasPosition("");
    setCurrentPage(1);
    setLoading(true);
    fetchMemberList("", "", "", "", "", "", "N", "", "", "", 1, PAGE_SIZE).then(res => {
      if (res.success) { setList(res.data); setTotal(res.total); }
    }).finally(() => setLoading(false));
  };

  if (page === "detail") {
    return (
      <>
        <Toast msg={toast} />
        <DetailPage
          item={selected}
          onBack={() => { loadList(); setPage("list"); }}
          onSave={handleSave}
          onDelete={handleDelete}
          isNew={isNew}
          isAdmin={isAdmin}
          userLevel={userLevel}
        />
      </>
    );
  }

  return (
    <div style={styles.wrap}>
      <Toast msg={toast} />

      <div style={styles.pageHeader}>
        <div><h1 style={styles.pageTitle}>👥 조합원 관리</h1></div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button style={{ background: "#fff", border: "1.5px solid #722ed1", color: "#722ed1", borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            onClick={() => setStatusReportOpen(true)}>📊 조합원 현황</button>
          <div style={styles.statBadge}>총 {total.toLocaleString()}명</div>
        </div>
      </div>

      {/* 검색 */}
      <div style={{ ...styles.filterCard, flexDirection: "column", gap: 10 }}>

        {/* 1행 */}
        <div style={styles.filterRow}>
          <div style={styles.filterField}>
            <span style={styles.filterLabel}>소속</span>
            <select style={{ ...styles.filterInput2, cursor: "pointer" }}
              value={searchDivSub} onChange={e => setSearchDivSub(e.target.value)}>
              <option value="">전체</option>
              {divisionSubList.map((s, i) => (
                <option key={i} value={s.division_sub_name}>{s.division_sub_name}</option>
              ))}
            </select>
          </div>

          <div style={styles.filterField}>
            <span style={styles.filterLabel}>지구</span>
            <input style={styles.filterInput2} value={searchSection}
              onChange={e => setSearchSection(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()} />
          </div>

          <div style={styles.filterField}>
            <span style={styles.filterLabel}>부서</span>
            <input style={styles.filterInput2} value={searchDepartment}
              onChange={e => setSearchDepartment(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()} />
          </div>

          <div style={styles.filterField}>
            <span style={styles.filterLabel}>직종</span>
            <select style={{ ...styles.filterInput2, cursor: "pointer" }}
              value={searchJobCode} onChange={e => setSearchJobCode(e.target.value)}>
              <option value="">전체</option>
              {jobCodeList.map((j, i) => (
                <option key={i} value={j.job_name}>{j.job_name}</option>
              ))}
            </select>
          </div>

          <div style={styles.filterField}>
            <span style={styles.filterLabel}>사번</span>
            <input style={styles.filterInput2} value={searchEmpNo}
              onChange={e => setSearchEmpNo(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()} />
          </div>

          <div style={styles.filterField}>
            <span style={styles.filterLabel}>성명</span>
            <input style={styles.filterInput2} value={searchName}
              onChange={e => setSearchName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()} />
          </div>
        </div>

        {/* 2행 */}
        <div style={styles.filterRow}>
          <div style={{ ...styles.filterField, flex: "1 1 240px" }}>
            <span style={styles.filterLabel}>가입일자</span>
            <input type="date" style={{ ...styles.filterInput2, flex: "1.4 1 0", cursor: "pointer" }}
              value={searchJoinDt ? `${searchJoinDt.slice(0,4)}-${searchJoinDt.slice(4,6)}-${searchJoinDt.slice(6,8)}` : ""}
              onChange={e => setSearchJoinDt(e.target.value.replace(/-/g, ""))}
              onKeyDown={e => e.preventDefault()}
              onClick={e => e.target.showPicker && e.target.showPicker()} />
            <select style={{ ...styles.filterInput2, flex: "0 0 70px", cursor: "pointer", borderLeft: "1px solid #e5e7eb" }}
              value={searchJoinDtCond} onChange={e => setSearchJoinDtCond(e.target.value)}>
              <option value="">전체</option>
              <option value="eq">일치</option>
              <option value="before">이전</option>
              <option value="after">이후</option>
            </select>
          </div>

          <div style={styles.filterField}>
            <span style={styles.filterLabel}>간부여부</span>
            <select style={{ ...styles.filterInput2, cursor: "pointer" }}
              value={searchHasPosition} onChange={e => setSearchHasPosition(e.target.value)}>
              <option value="">전체</option>
              <option value="Y">간부/대의원</option>
            </select>
          </div>

          <div style={styles.filterField}>
            <span style={styles.filterLabel}>가입상태</span>
            <select style={{ ...styles.filterInput2, cursor: "pointer" }}
              value={searchWithdrawYn} onChange={e => setSearchWithdrawYn(e.target.value)}>
              <option value="N">가입중</option>
              <option value="A">휴직</option>
              <option value="B">가입+휴직</option>
              <option value="Y">탈퇴</option>
              <option value="D">삭제</option>
              <option value="">전체</option>
            </select>
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
            <button style={styles.searchBtn} onClick={handleSearch}>검색</button>
            <button style={{ ...styles.searchBtn, background: "#888" }} onClick={handleReset}>초기화</button>
          </div>
        </div>
      </div>

      {/* 리스트 */}
      <div style={styles.card}>
        <div style={styles.tableHeader}>
                {["소속", "지구", "부서", "직종", "사번", "성명", "가입일자", "노동조합 직책", "상태"].map(h => (
                  <div key={h} style={styles.th}>{h}</div>
                ))}
                </div>
                {loading ? (
                  <div style={styles.empty}>⏳ 불러오는 중...</div>
                ) : list.length === 0 ? (
                  <div style={styles.empty}>조합원이 없습니다.</div>
                ) : list.map((item, i) => (
                  <div key={i} style={styles.tableRow}
          onClick={() => loadDetail(item.emp_no)}
          onMouseEnter={e => e.currentTarget.style.background = "#f8faff"}
          onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
          <div style={styles.td}>{item.division_sub_name || "-"}</div>
          <div style={styles.td}>{item.section           || "-"}</div>
          <div style={styles.td}>{item.department        || "-"}</div>
          <div style={styles.td}>{item.job_name          || "-"}</div>
          <div style={styles.td}>{item.emp_no}</div>
          <div style={{ ...styles.td, fontWeight: 600 }}>{item.name}</div>
          <div style={styles.td}>{item.join_dt ? `${item.join_dt.slice(0,4)}-${item.join_dt.slice(4,6)}-${item.join_dt.slice(6,8)}` : "-"}</div>
          <div style={styles.td}>{item.union_position || "-"}</div>
          <div style={styles.td}>
            {item.del_yn === 'Y'
              ? <span style={{ color: "#722ed1", fontWeight: 700 }}>삭제</span>
              : item.withdraw_yn === 'Y'
                ? <span style={{ color: "#ff4d4f", fontWeight: 700 }}>탈퇴</span>
              : item.withdraw_yn === 'A'
                ? <span style={{ color: "#ff6e00", fontWeight: 700 }}>휴직</span>
                : <span style={{ color: "#bbb" }}>-</span>
            }
          </div>
        </div>
        ))}
        {totalPages > 1 && (
          <div style={styles.pagination}>
            {/* 이전 */}
            <button
              style={{ ...styles.pageBtn, width: 'auto', padding: '0 12px', whiteSpace: 'nowrap', opacity: currentPage === 1 ? 0.4 : 1, cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}>
              ← 이전
            </button>

            {getPageNumbers(currentPage, totalPages).map((p, i) =>
              p === '...' ? (
                <span key={`ellipsis-${i}`} style={{ padding: "0 6px", color: "#aaa" }}>...</span>
              ) : (
                <button key={p}
                  style={{ ...styles.pageBtn, ...(p === currentPage ? styles.pageBtnActive : {}) }}
                  onClick={() => setCurrentPage(p)}>{p}</button>
              )
            )}

            {/* 다음 */}
            <button
              style={{ ...styles.pageBtn, width: 'auto', padding: '0 12px', whiteSpace: 'nowrap', opacity: currentPage === totalPages ? 0.4 : 1, cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}>
              다음 →
            </button>
          </div>
        )}
      </div>

      {/* 버튼 영역 */}
      {isAdmin && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>

            {/* 업로드 모드 선택 */}
            <div style={{ display: "flex", gap: 12, alignItems: "center", background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "8px 14px", fontSize: 13 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", color: uploadMode === 'add' ? "#1677ff" : "#888", fontWeight: uploadMode === 'add' ? 700 : 400 }}>
                <input type="radio" name="uploadMode" value="add"
                  checked={uploadMode === 'add'}
                  onChange={() => setUploadMode('add')}
                  style={{ accentColor: "#1677ff" }} />
                추가
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", color: uploadMode === 'delete' ? "#ff4d4f" : "#888", fontWeight: uploadMode === 'delete' ? 700 : 400 }}>
                <input type="radio" name="uploadMode" value="delete"
                  checked={uploadMode === 'delete'}
                  onChange={() => setUploadMode('delete')}
                  style={{ accentColor: "#ff4d4f" }} />
                삭제
              </label>
            </div>

            <input type="file" accept=".xlsx,.xls" ref={fileInputRef}
              style={{ display: "none" }} onChange={handleExcelUpload} />
            <button style={{
              ...styles.excelUploadBtn,
              borderColor: uploadMode === 'delete' ? "#ff4d4f" : "#1677ff",
              color: uploadMode === 'delete' ? "#ff4d4f" : "#1677ff"
            }} onClick={() => fileInputRef.current.click()} disabled={uploading}>
              {uploading ? "⏳ 업로드 중..." : "📂 엑셀 업로드"}
            </button>
            <button style={styles.excelDownBtn} onClick={handleDownloadTemplate}>📥 양식 다운로드</button>
            <button style={styles.excelDownBtn} onClick={handleExcelDownload}>📊 명단 다운로드</button>
            <button style={{ ...styles.excelDownBtn, borderColor: "#722ed1", color: "#722ed1" }}
              onClick={() => setVoterModalOpen(true)}>🗳️ 선거인명부</button>
          </div>
          <button style={styles.registerBtn}
            onClick={() => { setSelected(null); setIsNew(true); setPage("detail"); }}>
            ＋ 신규등록
          </button>
        </div>
      )}

      {/* 업로드 처리 중 — 화면 잠금 오버레이 */}
      {uploading && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 16
        }}>
          <div style={{
            width: 48, height: 48, border: '4px solid rgba(255,255,255,0.3)',
            borderTopColor: '#fff', borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />

          {uploadPhase === 'preprocessing' ? (
            <div style={{ color: '#fff', fontSize: 16, fontWeight: 600 }}>
              🔐 전처리 중입니다... (데이터 검증 · 암호화)
            </div>
          ) : (
            <>
              <div style={{ color: '#fff', fontSize: 16, fontWeight: 600 }}>
                💾 데이터베이스 처리 중입니다...
              </div>
              {uploadProgress.total > 0 && (
                <>
                  <div style={{ width: 280, height: 10, background: 'rgba(255,255,255,0.2)', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.min(100, Math.round((uploadProgress.processed / uploadProgress.total) * 100))}%`,
                      height: '100%', background: '#1677ff', transition: 'width 0.3s'
                    }} />
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}>
                    {uploadProgress.processed} / {uploadProgress.total} 건 처리 중
                  </div>
                </>
              )}
            </>
          )}

          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
            화면을 벗어나도 백그라운드에서 계속 처리됩니다.
          </div>
          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }
          `}</style>
        </div>
      )}

      {/* 업로드 결과 */}
      {uploadResult && (
        <div style={styles.uploadResult}>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>{uploadResult.message}</p>
          {uploadResult.errors?.length > 0 && (
            <table style={styles.errorTable}>
              <thead>
                <tr>
                  <th style={styles.errorTh}>행</th>
                  <th style={styles.errorTh}>사번</th>
                  <th style={styles.errorTh}>오류내용</th>
                </tr>
              </thead>
              <tbody>
                {uploadResult.errors.map((e, i) => (
                  <tr key={i}>
                    <td style={styles.errorTd}>{e.row}</td>
                    <td style={styles.errorTd}>{e.emp_no}</td>
                    <td style={styles.errorTd}>{e.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 선거인명부 처리 */}
      {voterModalOpen && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 380 }}>
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4, color: '#1a1a2e' }}>🗳️ 선거인명부 출력</div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 20 }}>명부 분리 기준을 선택해주세요.</div>

          {[
            { value: 'section_name',     label: '① 분리: 소속+지구 / 정렬: 부서+직종+성명' },
            { value: 'section_empno',    label: '② 분리: 소속+지구 / 정렬: 부서+직종+사번' },
            { value: 'department_name',  label: '③ 분리: 소속+부서 / 정렬: 직종+성명' },
            { value: 'department_empno', label: '④ 분리: 소속+부서 / 정렬: 직종+사번' },
            { value: 'division_name',    label: '⑤ 분리: 소속 / 정렬: 직종+성명' },
            { value: 'division_empno',   label: '⑥ 분리: 소속 / 정렬: 직종+사번' },
          ].map(opt => (
            <label key={opt.value}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', marginBottom: 8, borderRadius: 10, border: `1.5px solid ${voterCriteria === opt.value ? '#722ed1' : '#e0e0e0'}`, background: voterCriteria === opt.value ? '#f9f0ff' : '#fff', cursor: 'pointer' }}>
              <input type="radio" name="voterCriteria" value={opt.value}
                checked={voterCriteria === opt.value}
                onChange={() => setVoterCriteria(opt.value)} />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{opt.label}</span>
            </label>
          ))}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button
              style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: '1px solid #e0e0e0', background: '#fff', color: '#333', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              onClick={() => setVoterModalOpen(false)}
            >취소</button>
            <button
              style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', background: '#722ed1', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              onClick={() => { setVoterModalOpen(false); handleVoterPrint(voterCriteria); }}
            >🖨️ 출력</button>
          </div>
        </div>
      </div>
    )}

    {/* 조합원 현황 모달 호출 */}
    {statusReportOpen && (
      <StatusReportModal
        onClose={() => setStatusReportOpen(false)}
        onOpenQuotaManage={(refreshCallback) => {
          setOnQuotaSavedCallback(() => refreshCallback);
          setQuotaManageOpen(true);
        }}
      />
    )}

    {/* 정원 현원 관리 모달 호출 */}
    {quotaManageOpen && (
      <QuotaManageModal
        onClose={() => setQuotaManageOpen(false)}
        onSaved={onQuotaSavedCallback}
      />
    )}
    </div>
  );
}

// ===================== 스타일 =====================
const styles = {
  wrap        : { background: "#f5f7fa", padding: 24, fontFamily: "'Noto Sans KR', sans-serif" },
  pageHeader  : { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  pageTitle   : { fontSize: 22, fontWeight: 800, color: "#1a1a2e", margin: "0 0 4px" },
  statBadge   : { background: "#e8f4fd", color: "#1677ff", border: "1px solid #bae0ff", borderRadius: 8, padding: "6px 14px", fontSize: 13, fontWeight: 600 },
  filterCard  : { background: "#fff", borderRadius: 12, padding: "14px 16px", marginBottom: 12, display: "flex", gap: 10, flexWrap: "wrap", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  searchInput : { flex: 1, border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "4px 4px", fontSize: 14, outline: "none", background: "#fafafa" },
  searchBtn   : { background: "#1677ff", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  card        : { background: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" },
  tableHeader : { display: "grid", gridTemplateColumns: "1.3fr 0.8fr 1.3fr 1.3fr 0.9fr 1.3fr 1fr 1fr 0.6fr", padding: "12px 20px", background: "#f8faff", borderBottom: "1px solid #e8f0fe" },
  th          : { fontSize: 12, fontWeight: 700, color: "#888" },
  tableRow    : { display: "grid", gridTemplateColumns: "1.3fr 0.8fr 1.3fr 1.3fr 0.9fr 1.3fr 1fr 1fr 0.6fr", padding: "14px 20px", borderBottom: "1px solid #f5f5f5", cursor: "pointer", transition: "background 0.15s" },
  td          : { fontSize: 14, color: "#1a1a2e", display: "flex", alignItems: "center" },
  empty       : { textAlign: "center", padding: "60px 0", color: "#bbb", fontSize: 14 },
  pagination  : { display: "flex", justifyContent: "center", gap: 6, padding: "14px 0" },
  pageBtn     : { width: 30, height: 30, borderRadius: 6, border: "1px solid #e0e0e0", background: "#fff", cursor: "pointer", fontSize: 13, color: "#666" },
  pageBtnActive: { background: "#1677ff", color: "#fff", border: "1px solid #1677ff" },
  registerBtn : { background: "linear-gradient(135deg, #1677ff, #4096ff)", color: "#fff", border: "none", borderRadius: 10, padding: "12px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 12px rgba(22,119,255,0.35)" },
  backBtn     : { background: "none", border: "1px solid #e0e0e0", borderRadius: 8, padding: "8px 14px", fontSize: 13, color: "#666", cursor: "pointer", marginBottom: 12, display: "block" },
  editBtn     : { background: "#fff", border: "1.5px solid #1677ff", color: "#1677ff", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  deleteBtn   : { background: "#fff2f0", border: "1.5px solid #ff4d4f", color: "#ff4d4f", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  printBtn    : { background: "#fff", border: "1.5px solid #52c41a", color: "#52c41a", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  resetPwBtn  : { background: "#fff7e6", border: "1.5px solid #fa8c16", color: "#fa8c16", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  excelUploadBtn : { background: "#fff", border: "1.5px solid #1677ff", color: "#1677ff", borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  excelDownBtn   : { background: "#fff", border: "1.5px solid #52c41a", color: "#52c41a", borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  uploadResult   : { marginTop: 12, padding: 14, background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 10, fontSize: 13 },
  errorTable     : { width: "100%", borderCollapse: "collapse", marginTop: 8 },
  errorTh        : { background: "#fff2f0", border: "1px solid #ffccc7", padding: "6px 10px", fontSize: 12, textAlign: "left" },
  errorTd        : { border: "1px solid #f0f0f0", padding: "6px 10px", fontSize: 12 },
  filterRow    : { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" },
  filterField  : { display: "flex", alignItems: "center", border: "1.5px solid #e5e7eb", borderRadius: 8, overflow: "hidden", background: "#fff", flex: "1 1 150px", height: 38 },
  filterLabel  : { padding: "0 10px", height: "100%", display: "flex", alignItems: "center", background: "#f0f5ff", color: "#4c6ef5", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 },
  filterInput2 : { border: "none", outline: "none", padding: "0 10px", height: "100%", fontSize: 13, fontFamily: "inherit", flex: 1, minWidth: 0, background: "transparent", color: "#1a1a2e" },
};

const detailStyles = {
  grid        : { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  field       : { display: "flex", flexDirection: "row", alignItems: "center", gap: 0, background: "#fafafa", borderRadius: 8, border: "1.5px solid #e0e0e0", overflow: "hidden" },
  label       : { fontSize: 13, fontWeight: 600, color: "#666", background: "#f0f4ff", padding: "12px 16px", minWidth: 190, whiteSpace: "nowrap", borderRight: "1.5px solid #e0e0e0" },
  input       : { border: "none", borderRadius: 0, padding: "12px 16px", fontSize: 14, outline: "none", background: "transparent", color: "#1a1a2e", width: "100%", boxSizing: "border-box" },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: "#1677ff", borderLeft: "3px solid #1677ff", paddingLeft: 10, marginBottom: 12 },
};

const ms = {
  overlay  : { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" },
  modal    : { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 460, margin: "0 16px", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden" },
  cancelBtn: { background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, padding: "10px 20px", fontSize: 14, color: "#666", cursor: "pointer" },
  saveBtn  : { border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, color: "#fff", fontWeight: 700, cursor: "pointer" },
};

const sr = {
  th: { padding: "8px 10px", borderBottom: "2px solid #e0e0e0", background: "#f8faff", fontWeight: 700, color: "#444" },
  subTh: { padding: "6px 10px", borderBottom: "1px solid #e0e0e0", background: "#fafcff", fontWeight: 600, color: "#888", fontSize: 12, textAlign: "right" },
  td: { padding: "8px 10px", borderBottom: "1px solid #f0f0f0", textAlign: "right", color: "#1a1a2e" },
  tdLabel: { padding: "8px 10px", borderBottom: "1px solid #f0f0f0", fontWeight: 600, color: "#1a1a2e" },
};

const qm = {
  input: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "7px 10px", fontSize: 13, outline: "none" },
  miniBtn: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "7px 10px", fontSize: 13, background: "#f5f5f5", cursor: "pointer", flexShrink: 0 },
};